import { type ChangeEvent, useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useDispatch } from 'react-redux';

import {
  freshStrawRestApi,
  useImportFreshStrawFreshEmbryosBatchMutation,
  useImportFreshStrawFreshEmbryosMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useImportFreshEmbryosBatchV3Mutation } from '@/services/fresh-shipment';
import { isAbortError } from '@/services/progress-stream';

type UploadTransferPregCheckProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

const MAX_FILE_SIZE_BYTES = 20_000_000;

export const UploadTransferPregCheck = ({
  visible,
  setVisible,
}: UploadTransferPregCheckProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const useBatchUpload = useFeatureFlag('Batch_Upload_Fresh_Embryos');
  const isOverrideEnabled = useFeatureFlag(
    'Fresh_Shipment_Billable_Shipped_Update',
  );
  const isProgramNameEnabled = useFeatureFlag(
    'Fresh_Shipment_Export_Program_Name',
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  const [importFreshEmbryos, { isLoading: isLoadingStandard }] =
    useImportFreshStrawFreshEmbryosMutation();
  const [importFreshEmbryosBatch, { isLoading: isLoadingBatch }] =
    useImportFreshStrawFreshEmbryosBatchMutation();
  const { mutateAsync: importFreshEmbryosV3, isPending: isLoadingV2 } =
    useImportFreshEmbryosBatchV3Mutation();

  const isLoading = isLoadingStandard || isLoadingBatch || isLoadingV2;

  const handleFileSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      if (file && file.size > MAX_FILE_SIZE_BYTES) {
        setSelectedFile(null);
        setUploadErrors([`${file.name} is larger than the 20 MB limit.`]);
        event.target.value = '';
        return;
      }
      setSelectedFile(file);
      setUploadErrors([]);
    },
    [],
  );

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setUploadErrors([]);
    if (fileUploadRef.current) fileUploadRef.current.value = '';
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const importMutation = useBatchUpload
        ? importFreshEmbryosBatch
        : importFreshEmbryos;
      const result = await importMutation({ file: selectedFile }).unwrap();

      const skippedCount = result.skipped ?? 0;
      if (result.errors.length > 0 || skippedCount > 0) {
        if (result.errors.length > 0) setUploadErrors(result.errors);
        dispatch(
          addToast({
            severity: 'warn',
            summary:
              result.errors.length > 0
                ? 'Upload Complete with Errors'
                : 'Upload Complete',
            detail: `Parsed: ${result.parsed}, Saved: ${result.saved}${skippedCount > 0 ? `, Skipped: ${skippedCount}` : ''}${result.errors.length > 0 ? `, Errors: ${result.errors.length}` : ''}`,
            life: ToastLife.LONG,
          }),
        );
        if (result.errors.length === 0) {
          resetForm();
          setVisible(false);
        }
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Upload Complete',
            detail: `Parsed: ${result.parsed}, Saved: ${result.saved}`,
            life: ToastLife.DEFAULT,
          }),
        );
        resetForm();
        setVisible(false);
      }
    } catch (err) {
      const raw = (err as { data?: string })?.data;
      let message = 'There was an error uploading the data.';
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw) as { message?: string };
          if (parsed.message) message = parsed.message;
        } catch {
          message = raw;
        }
      }
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Upload Failed',
          detail: message,
          life: ToastLife.LONG,
        }),
      );
    }
  }, [
    selectedFile,
    useBatchUpload,
    importFreshEmbryosBatch,
    importFreshEmbryos,
    dispatch,
    resetForm,
    setVisible,
  ]);

  const handleUploadV2 = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const result = await importFreshEmbryosV3({ file: selectedFile });

      dispatch(
        freshStrawRestApi.util.invalidateTags([
          'FertilizationListFreshShipment',
        ]),
      );
      void queryClient.invalidateQueries({
        queryKey: ['fresh-straw', 'fertilizations', 'v2'],
      });

      const skippedCount = result.skipped ?? 0;
      const updatedCount = result.updated ?? 0;
      const hasWarnings = result.errors.length > 0 || skippedCount > 0;

      if (hasWarnings) {
        if (result.errors.length > 0) setUploadErrors(result.errors);

        const details: string[] = [
          `Parsed: ${result.parsed}`,
          `Saved: ${result.saved}`,
        ];
        if (updatedCount > 0) details.push(`Updated: ${updatedCount}`);
        if (skippedCount > 0) details.push(`Skipped: ${skippedCount}`);
        if (result.errors.length > 0)
          details.push(`Errors: ${result.errors.length}`);

        dispatch(
          addToast({
            severity: 'warn',
            summary:
              result.errors.length > 0
                ? 'Upload Complete with Errors'
                : 'Upload Complete',
            detail: details.join(', '),
            life: ToastLife.LONG,
          }),
        );
        if (result.errors.length === 0) {
          resetForm();
          setVisible(false);
        }
      } else {
        const details: string[] = [
          `Parsed: ${result.parsed}`,
          `Saved: ${result.saved}`,
        ];
        if (updatedCount > 0) details.push(`Updated: ${updatedCount}`);

        dispatch(
          addToast({
            severity: 'success',
            summary: 'Upload Complete',
            detail: details.join(', '),
            life: ToastLife.DEFAULT,
          }),
        );
        resetForm();
        setVisible(false);
      }
    } catch (err) {
      // The user hit Cancel on the progress toast, which already reported it.
      if (isAbortError(err)) return;

      const raw = (err as { data?: string })?.data;
      let message = 'There was an error uploading the data.';
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw) as { message?: string };
          if (parsed.message) message = parsed.message;
        } catch {
          message = raw;
        }
      }
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Upload Failed',
          detail: message,
          life: ToastLife.LONG,
        }),
      );
    }
  }, [
    selectedFile,
    importFreshEmbryosV3,
    dispatch,
    queryClient,
    resetForm,
    setVisible,
  ]);

  const handleUploadV2WithConfirm = useCallback(() => {
    if (!selectedFile) return;

    setIsOverrideDialogOpen(true);
  }, [selectedFile]);

  const handleOverrideConfirm = useCallback(() => {
    setIsOverrideDialogOpen(false);
    void handleUploadV2();
  }, [handleUploadV2]);

  const handleHide = useCallback(() => {
    resetForm();
    setVisible(false);
  }, [resetForm, setVisible]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleHide();
    },
    [handleHide],
  );

  return (
    <>
      <ConfirmDialog />
      <Dialog open={visible} onOpenChange={handleOpenChange}>
        <DialogContent className="md:max-w-[40vw]">
          <DialogHeader>
            <DialogTitle>Upload Fresh Embryos</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="upload-fresh-embryos-file">
                Workbook (.xlsx, .xls, .csv)
              </Label>
              <Input
                ref={fileUploadRef}
                id="upload-fresh-embryos-file"
                name="file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                disabled={isLoading}
                className="file:bg-muted file:text-foreground hover:file:bg-muted/80 h-10 cursor-pointer py-1.5 file:mr-3 file:cursor-pointer file:rounded-md file:px-3"
              />
              <p className="text-muted-foreground text-xs">
                Max file size 20 MB.
              </p>
            </div>

            {selectedFile && (
              <p className="text-sm text-green-600">
                {selectedFile.name} ready to upload.
              </p>
            )}

            {uploadErrors.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded border border-red-300 bg-red-50 p-3">
                <p className="mb-1 text-sm font-medium text-red-700">
                  Errors ({uploadErrors.length}):
                </p>
                <ul className="list-inside list-disc text-sm text-red-600">
                  {uploadErrors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleHide}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!selectedFile || isLoading}
              onClick={
                isProgramNameEnabled || isOverrideEnabled
                  ? handleUploadV2WithConfirm
                  : handleUpload
              }
            >
              {isLoading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
          {isProgramNameEnabled && (
            <AlertDialog
              open={isOverrideDialogOpen}
              onOpenChange={setIsOverrideDialogOpen}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Override Existing Data</AlertDialogTitle>
                  <AlertDialogDescription>
                    If embryo data already exists, it will be overridden with
                    the uploaded values. Do you want to continue?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleOverrideConfirm}>
                    Upload &amp; Override
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
